/* eslint-disable monorepo/no-internal-import */
import {AB_TEST_HEADERS} from '@aboutyou/feature-flags/src/abtests/types';
import {useIsStorageConsentGiven} from '@aboutyou/storage';
import {usePrevious} from '@aboutyou/utils';
import React, {FC, useEffect, useMemo, useRef} from 'react';
import {generateCacheKey} from '../generateCacheKey';
import {useQueryClient} from '../react-query';
import {DataFetcherRequest} from '../useRequest';
import {useGrpcClientConfig} from './GrpcClientContext';
import {GrpcTrailersConfig} from './useGenericGrpcRequest';

type RequestsWithPendingAbTestActivations = Map<
  string,
  {
    possibleAbTests: string[];
    invalidate: () => void;
  }
>;

type Context = React.MutableRefObject<RequestsWithPendingAbTestActivations>;

const ctx = React.createContext<Context | null>(null);

/**
 * We need to refetch the BE requests with pending ab test activations after the user accepts the cookie consent.
 * The pending activation are determined by the x-tadarida-possible-ab-tests trailer in each request.
 * If a request has a pending ab test activation based on the trailer and that AB Test is currently active for the user, then the request will be refetched with the updated config.
 */
export const RefetchRequestsWithPendingAbTestActivations: FC<
  React.PropsWithChildren
> = ({children}) => {
  const requestsWithPossibleAbTestsBeforeCookieConsent =
    useRef<RequestsWithPendingAbTestActivations>(new Map());

  const addedAbTestKeys = useAddedAbTestKeys();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (addedAbTestKeys.length) {
      /**
       * Here we are refetching the requests with pending ab test activations after the user accepts the cookie consent.
       * that would mean the users has not seen the view where the abTest is activated yet.
       * We refetch those requests with the updated config. But this needs to happen only after the the useGrpcRequest instances has been updated with the new abTest config.
       *
       * We are using a setTimeout here to make sure the useGrpcRequest instances has been updated with the new abTest config.
       * This is not ideal because we cant be sure how long it will take for the useGrpcRequest instances to update.
       * But from a lot of manual testing it seems to work fine, even with cpu throttled simulation.
       */
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      timer = setTimeout(() => {
        const requests = requestsWithPossibleAbTestsBeforeCookieConsent.current;

        // one invalidation per cache key: it refetches the key while it is observed
        // and otherwise marks it stale for the next time it is observed
        requests.forEach(({possibleAbTests, invalidate}, cacheKey) => {
          if (!possibleAbTests.some(key => addedAbTestKeys.includes(key))) {
            return;
          }

          invalidate();
          requests.delete(cacheKey);
        });
      }, 300);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [addedAbTestKeys]);

  return (
    <ctx.Provider value={requestsWithPossibleAbTestsBeforeCookieConsent}>
      {children}
    </ctx.Provider>
  );
};

export const useRememberRequestsWithPossibleAbTestsBeforeCookieConsent = ({
  request,
  cacheKey,
}: {
  cacheKey: string;
  request: DataFetcherRequest<any, unknown>;
}) => {
  const isConsentBannerAccepted = useIsStorageConsentGiven();
  const queryClient = useQueryClient();

  const requestsWithPossibleAbTestsBeforeCookieConsent = useContextValue();

  useEffect(() => {
    if (isConsentBannerAccepted) return;

    const possibleAbTestsFromGrpcTrailers = getPossibleAbTestsFromGrpcTrailers(
      (request as DataFetcherRequest<GrpcTrailersConfig>).data?.trailers,
    );
    if (!possibleAbTestsFromGrpcTrailers.length) return;

    requestsWithPossibleAbTestsBeforeCookieConsent.current.set(cacheKey, {
      possibleAbTests: possibleAbTestsFromGrpcTrailers,
      invalidate: () =>
        queryClient.invalidateQueries({
          queryKey: [generateCacheKey(cacheKey)],
          exact: true,
        }),
    });
  }, [
    cacheKey,
    isConsentBannerAccepted,
    queryClient,
    request,
    requestsWithPossibleAbTestsBeforeCookieConsent,
  ]);
};

// those are not a real ab_test, do not refetch the requests due to this, this ab_test would never get activated on the client
const NOT_A_REAL_AB_TEST = [
  'ab_coupons_rollout',
  'ab_coins_rollout',
  'ab_price_display_overhaul_v2',
];

/**
 * Only AB tests that this config update adds can turn a pending activation into a real one.
 * A test that was already in the config was sent with the request and applied by the backend,
 * so it must not refetch anything, even when the trailers still list it as possible.
 */
const useAddedAbTestKeys = () => {
  const grpcConfig = useGrpcClientConfig();
  const currentAbTestKeys = useMemo(
    () =>
      grpcConfig.abTests
        .map(e => e.key)
        .filter(key => !NOT_A_REAL_AB_TEST.includes(key))
        .join(','),
    [grpcConfig],
  );
  const previousAbTestKeys = usePrevious(currentAbTestKeys);

  return useMemo(() => {
    const previous = new Set(previousAbTestKeys?.split(',') ?? []);

    return currentAbTestKeys
      .split(',')
      .filter(key => key && !previous.has(key));
  }, [currentAbTestKeys, previousAbTestKeys]);
};

const useContextValue = () => {
  const ctxValue = React.useContext(ctx);
  if (ctxValue === null) {
    throw new Error(
      'Missing context value. Please use the RefetchRequestsWithPendingAbTestActivations',
    );
  }
  return ctxValue;
};

const getPossibleAbTestsFromGrpcTrailers = (
  grpcTrailers?: GrpcTrailersConfig['trailers'],
) => {
  if (grpcTrailers && AB_TEST_HEADERS.TadaridaPossibleABTests in grpcTrailers) {
    return grpcTrailers[AB_TEST_HEADERS.TadaridaPossibleABTests] as string[];
  }

  return [];
};
